import {type Ping, type PingResults, Servers} from "@/modules/trouble";
import axios from "axios";
import {troubleService} from "@/global/api/gen";
import {useMemo, useState} from "react";

const generateTestData = (sizeMB: number) => {
    const length = sizeMB * 1024 * 1024;
    const array = new Uint8Array(length);
    for (let i = 0; i < length; i++) {
        array[i] = Math.floor(Math.random() * 256);
    }
    return array;
};

export const useSpeedTest = () => {
    const defaultPing = useMemo(() => Object.entries(Servers).reduce((el, [name, url]) => {
        return {
            ...el,
            [name]: {
                url,
                ping: []
            }
        };
    }, {}) as PingResults, [Servers]);

    const [pingResults, setPingResults] = useState<PingResults>(defaultPing);
    const [totalPing, setTotalPing] = useState<Ping>({
        iteration: 0,
        pingTime: 0
    });
    const [uploadSpeed, setUploadSpeed] = useState<number>(0);
    const [downloadSpeed, setDownloadSpeed] = useState<number>(0);
    const [isTestRunning, setIsTestRunning] = useState(false);

    const [currentPhase, setCurrentPhase] = useState<"idle" | "ping" | "download" | "upload" | "complete">("idle");
    const [progress, setProgress] = useState<number>(0);

    const runPingTest = async (iterations = 10) => {
        const pingTestResult: PingResults = JSON.parse(JSON.stringify(defaultPing));

        await Promise.all(Object.entries(pingTestResult).map(async ([_key, value]) => {
            for (let i = 0; i < iterations; i++) {
                const start = performance.now();

                try {
                    await axios.get(`https://${value.url}/`,  {
                        timeout: 5000
                    });
                    const end = performance.now();
                    const pingTime = (end - start);
                    value.ping.push({
                        iteration: i + 1,
                        pingTime: +(pingTime.toFixed(2))
                    });
                    setTotalPing((prev) => {
                        prev.pingTime += +(pingTime.toFixed(2));
                        prev.iteration += 1;
                        return prev;
                    });
                } catch (error) {
                    if (axios.isAxiosError(error) && !!error.status) {
                        const end = performance.now();
                        const pingTime = end - start;

                        value.ping.push({
                            iteration: i + 1,
                            pingTime: +(pingTime.toFixed(2))
                        });

                        setTotalPing((prev) => {
                            prev.pingTime += +(pingTime.toFixed(2));
                            prev.iteration += 1;
                            return prev;
                        });
                    }
                }

                setPingResults({...pingTestResult});
            }
        }));
    };

    const runUploadTest = async (sizeMB = 10) => {
        try {
            const testData = generateTestData(sizeMB);
            const blob = new Blob([testData]);

            const start = performance.now();

            await troubleService.troubleControllerUploadTest({file: blob}, {
                onUploadProgress: (progressEvent) => {
                    setUploadSpeed(progressEvent.loaded / (performance.now() - start) / 1000 * 8);
                }
            });

            const end = performance.now();

            const durationSeconds = (end - start) / 1000;
            const speedMbps = (sizeMB * 8) / durationSeconds;

            setUploadSpeed(speedMbps);

            return speedMbps;
        } catch (error) {
            return 0;
        }
    };

    const runDownloadTest = async () => {
        try {
            const start = performance.now();

            const response = await troubleService.troubleControllerDownloadTest({
                onDownloadProgress: (progressEvent) => {
                    setDownloadSpeed(progressEvent.loaded / (performance.now() - start) / 1000 * 8);
                }
            });

            const end = performance.now();

            const durationSeconds = (end - start) / 1000;
            const contentLength = response.headers?.["content-length"] ?? 0;
            const contentLengthmB = +contentLength * 8 / 1000 / 1000;
            const speedMbps = contentLengthmB / durationSeconds;

            setDownloadSpeed(speedMbps);

            return speedMbps;
        } catch (error) {
            return 0;
        }
    };

    const startSpeedTest = async () => {
        setIsTestRunning(true);
        setProgress(0);
        setPingResults(defaultPing);
        setUploadSpeed(0);
        setDownloadSpeed(0);
        setCurrentPhase('ping');

        await runPingTest();
        setProgress(20);
        setCurrentPhase('upload');
        await runUploadTest();
        setProgress(60);
        setCurrentPhase('download');
        await runDownloadTest();

        setProgress(100);
        setIsTestRunning(false);
        setCurrentPhase('complete');
    };

    return {
        uploadSpeed,
        downloadSpeed,
        totalPing,
        pingResults,
        isTestRunning,
        currentPhase,
        progress,
        startSpeedTest
    };
};